import { CourseMarkdown } from "@/components/storyboard/CourseMarkdown";
import type { VideoElementData } from "@/types/storyboard";

/**
 * Builds the privacy-friendly embed URL for an external video.
 * YouTube videos use the nocookie domain, Vimeo gets dnt=1 and the
 * privacy hash (h=...) required for unlisted videos.
 */
export const buildVideoEmbedUrl = (video: VideoElementData): string => {
  if (video.provider === "YOUTUBE") {
    return `https://www.youtube-nocookie.com/embed/${video.videoId}`;
  }
  const params = new URLSearchParams({ dnt: "1" });
  if (video.embedHash) {
    params.set("h", video.embedHash);
  }
  return `https://player.vimeo.com/video/${video.videoId}?${params.toString()}`;
};

interface Props {
  video: VideoElementData;
}

export const VideoElement = ({ video }: Props) => {
  if (!video?.videoId || !video?.provider) {
    return null;
  }

  return (
    <div className="prose max-w-full">
      {video.title && <h2>{video.title}</h2>}
      {video.introduction && (
        <CourseMarkdown>{video.introduction}</CourseMarkdown>
      )}
      {/* Capped at 640px like labeled graphics, so the player doesn't grow
          with the available space on wide screens. */}
      <div className="not-prose aspect-video w-full max-w-160 overflow-hidden rounded-lg bg-muted">
        <iframe
          src={buildVideoEmbedUrl(video)}
          title={video.title || `${video.provider} Video`}
          className="h-full w-full border-0"
          allow="autoplay; fullscreen; picture-in-picture; encrypted-media; clipboard-write"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </div>
    </div>
  );
};

export default VideoElement;
